'use client'
import { mainStore } from '@/domains/Account/store'
import { User } from '@/services/Api/generated'
import { resolutionEventDispatcher } from '@/services/ResolutionEventDispatcher'
import { POPUP_TYPE } from '@/store/Popup'
import { reaction } from 'mobx'
import {
  usePathname,
  useRouter,
  useSearchParams,
  useSelectedLayoutSegments,
} from 'next/navigation'
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'

interface InitialAppOptions {
  // Пользователь, полученный на сервере из cookie (getServerUser)
  initialUser?: User | null
}

export function useInitialApp({ initialUser }: InitialAppOptions = {}) {
  const router = useRouter()
  const pathname = usePathname()
  const pathnameSegments = useSelectedLayoutSegments()
  const searchParams = useSearchParams()

  const isAuthPage = useMemo(() => {
    return pathnameSegments[0] === 'auth'
  }, [pathnameSegments])

  const isHomePage = useMemo(() => {
    return pathname === '/'
  }, [pathname])

  const getReturnUrl = useCallback(() => {
    if (isAuthPage || isHomePage) {
      return ''
    }

    const params = new URLSearchParams(searchParams)
    const queryString = params.toString()
    return queryString ? `${pathname}?${queryString}` : pathname
  }, [isAuthPage, isHomePage, pathname, searchParams])

  // Гидрация пользователя, полученного на сервере из cookie.
  // Выполняется до первой отрисовки (useLayoutEffect), чтобы шапка
  // обновилась без мигания. Отдельный запрос авторизации не нужен:
  // доступ уже проверен серверным guard-ом (layout.tsx + getServerUser).
  useLayoutEffect(() => {
    if (initialUser != null && mainStore.user.uuid == null) {
      mainStore.user.setData(initialUser)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Разлогин посреди сессии (401 в fetchWithAuth → clearOnLogout):
  // отправляем на страницу входа с возвратом на текущий путь.
  useEffect(() => {
    const returnUrl = getReturnUrl()
    const returnParam = returnUrl
      ? `?returnUrl=${encodeURIComponent(returnUrl)}`
      : ''

    return reaction(
      () => mainStore.user.uuid,
      (userUuid) => {
        if (userUuid == null) {
          router.push('/auth/login' + returnParam)
        }
      }
    )
  }, [getReturnUrl, isAuthPage, isHomePage, pathname, router])

  useEffect(() => {
    resolutionEventDispatcher.recalculate()
  }, [])
}
